import type { AIHeaderImageStatus, GenerateImageProps } from '@atlassian/ai-image-generation';

import type { COVERPICTUREWIDTH } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';

import type { CoverPictureStateContainer } from './CoverPictureStateContainer.ts';
import type { UnsplashPhoto } from './EditorCoverPicture/unsplashAPI.ts';

export type FloatingToolbarButtonStyleType = {
	height: string;
	alignItems: 'center';
};

export enum CoverPictureIdPropertyKey {
	PUBLISHED = 'cover-picture-id-published',
	DRAFT = 'cover-picture-id-draft',
}

export type CoverPictureIdContentProperty = {
	key: CoverPictureIdPropertyKey;
	value: string;
	version?: {
		number: number;
	};
};

type GraphqlCoverPictureIdContentProperty = {
	nodes: CoverPictureIdContentProperty[];
};

export type GraphqlQueryDraftCoverPictureId = {
	coverPictureIdDraft: GraphqlCoverPictureIdContentProperty;
};

export type GraphqlQueryContentPublishedCoverPictureId = {
	coverPictureIdPublished: GraphqlCoverPictureIdContentProperty;
};

export type CoverPictureId = {
	value: {
		id: string;
		position: number;
	};
	version: number;
};

export type EditorPageCoverPictureEntryComponentProps = {
	contentId: string;
	isTitleHovered: boolean;
	isPublishingContent: boolean;
	userOffline: boolean;
	contentType: string;
	coverPictureState: CoverPictureStateContainer;
	isUnpublishedDraft: boolean;
	floatingToolbarButtonStyle?: FloatingToolbarButtonStyleType;
	isFixedWidthImageOption?: boolean;
	width?: number;
	leftOffset?: number;
	dynamicCoverImageHeight?: number;
	isReadOnly?: boolean;
	hasCoverPicture: boolean;
	aiSuggestedUnsplashImages?: UnsplashPhoto[];
	isUnsplash?: boolean;
	aiHeaderImageStatus?: AIHeaderImageStatus;
	aiHeaderImageFileId?: string;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<void>;
	setAIHeaderImageStatus?: (status: AIHeaderImageStatus) => void;
};

export type EditorPageCoverPictureComponentProps = {
	contentId: string;
	isTitleHovered?: boolean;
	isPublishingContent: boolean;
	contentType: string;
	userOffline: boolean;
	hasCoverPicture: boolean;
	coverPictureState: CoverPictureStateContainer;
	isUnpublishedDraft: boolean;
	floatingToolbarButtonStyle?: FloatingToolbarButtonStyleType;
	width?: number;
	dynamicCoverImageHeight?: number;
	isReadOnly?: boolean;
	spaceKey?: string;
	isFixedWidthImageOption?: boolean;
	aiSuggestedUnsplashImages?: UnsplashPhoto[];
	isUnsplash?: boolean;
	aiHeaderImageStatus?: AIHeaderImageStatus;
	aiHeaderImageFileId?: string;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<void>;
	setAIHeaderImageStatus?: (status: AIHeaderImageStatus) => void;
};

export type ImagePickerComponentProps = {
	hasCoverPicture: boolean;
	isTitleHovered?: boolean;
	openMediaBrowser: () => void;
	userOffline?: boolean;
	isUploadingPicture: boolean;
	isRegeneratingImage: boolean;
	onRegenerate: () => void;
	onRemoveImage: () => void;
	onAcceptAIUnsplashImage: () => void;
	onImageWidthChange?: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	handleUnsplashUpload: (photoURL: string) => void;
	setIsUploadingPicture: (isUploadingPicture: boolean) => void;
	initialPicturePosition: number | undefined;
	onSaveReposition: (position: number) => void;
	shouldShowReposition: boolean;
	setShouldShowReposition: (shouldShowReposition: boolean) => void;
	coverPictureState: CoverPictureStateContainer;
	floatingToolbarButtonStyle?: FloatingToolbarButtonStyleType;
	forceUpdate?: (number: number) => void;
	contentId: string;
	dynamicCoverImageHeight?: number;
	spaceKey?: string;
	shouldShowAIUnsplashHeaderToolbar: boolean;
	isFixedWidthImageOption?: boolean;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<void>;
	// AI Image Acceptance Flow props
	shouldShowAIImageAcceptanceFlow: boolean;
	onAcceptAIGeneratedImage: () => void;
	onRetryAIGeneratedImage: () => void;
	isRetryingAIImage: boolean;
};

export type AddHeaderImageButtonProps = {
	userOffline?: boolean;
	openModal: () => void;
	closeModal: () => void;
	openMediaBrowser: () => void;
	isModalOpenState: boolean;
	handleUnsplashUpload: (photoURL: string) => void;
	setIsUploadingPicture: (isUploadingPicture: boolean) => void;
	initialUnsplashInfoArray: Array<object>;
	setInitialUnsplashInfoArray: (unsplashArray: Array<object>) => void;
	coverPictureState: CoverPictureStateContainer;
	floatingToolbarButtonStyle?: FloatingToolbarButtonStyleType;
	contentId: string;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<void>;
};

export type HoverOverlayAndDropDownItemsComponentProps = {
	userOffline?: boolean;
	closeModal: () => void;
	openMediaBrowser: () => void;
	setIsRepositionActive: (isRepositionActive: boolean) => void;
	onRemoveImage: () => void;
	onImageWidthChange?: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	handleUnsplashUpload: (photoURL: string) => void;
	setIsUploadingPicture: (isUploadingPicture: boolean) => void;
	initialUnsplashInfoArray: Array<object>;
	setInitialUnsplashInfoArray: (unsplashArray: Array<object>) => void;
	coverPictureState: CoverPictureStateContainer;
	contentId: string;
	dynamicCoverImageHeight?: number;
	spaceKey?: string;
	isFixedWidthImageOption?: boolean;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<void>;
};

export type RepositionImageComponentProps = {
	initialPicturePosition: number | undefined;
	onSaveReposition: (position: number) => void;
	setIsRepositionActive: (isRepositionActive: boolean) => void;
	setShouldShowReposition: (shouldShowReposition: boolean) => void;
	coverPictureState: CoverPictureStateContainer;
	forceUpdate?: (number: number) => void;
	userOffline?: boolean;
	dynamicCoverImageHeight?: number;
};

export type AIImageAcceptanceRepositionerProps = {
	initialPicturePosition: number | undefined;
	onSaveReposition: (position: number) => void;
	onAccept: () => void;
	onDiscard: () => void;
	onRetry: () => void;
	isRetrying: boolean;
	coverPictureState: CoverPictureStateContainer;
	forceUpdate?: (number: number) => void;
	userOffline?: boolean;
	dynamicCoverImageHeight?: number;
};

export type RendererPageCoverPictureComponentProps = {
	contentId?: any;
	isPagePreview: boolean;
	isSpaceOverview?: boolean;
	coverPicture: {
		draft: CoverPictureId | null;
		published: CoverPictureId | null;
	};
	contentAppearance: { draft: string; published: string };
	hasEmoji?: boolean;
	leftRightPaddingValue?: number;
	isPresenterMode?: boolean;
	coverPictureWidthOverride?: COVERPICTUREWIDTH;
	isDiffEditor?: boolean;
	useDraftData?: boolean;
};

export type PreviewPageCoverPictureComponentProps = {
	contentId?: string;
	contentAppearance: string;
	leftRightPaddingValue?: number;
};
